<template>
    <div
            v-if="!hidden && !isLoading && propertyAppResourcesLen"
            class="property-listing-card-pagination"
            :style="getClassStyle"
            ref="propertyListingContainer"
    >
        <property-listing-header
                :total="total"
                :address="searchedAddress"
        />
        <b-card-group
                v-if="propertyAppResourcesLen"
                class="property-listing"
        >
            <component
                v-for="(propertyAppResource, index) in propertyAppResources"
                :key="propertyAppResource._uuid || `card-${index}`"
                :property-app-resource="propertyAppResource"
                :property-details-route="propertyDetailsRoute"
                :favorite-option-enabled="favoriteOptionEnabled"
                :is="getCardType(propertyAppResource)"
            />
        </b-card-group>
        <template v-if="propertyAppResourcesLen && total > propertyAppResourcesLen">
            <b-pagination
                class="mt-3 mb-0"
                :total-rows="total"
                :per-page="pageSize"
                :value="currentPage"
                @change="moveToPage"
                align="center"
                first-number
                last-number
                :prev-class="prevBtnClass"
                :next-class="nextBtnClass"
                ellipsis-class="page-separator d-flex align-items-center flex-column"
            >
                <template slot="prev-text">
                    <i class="far fa-angle-left" />
                </template>
                <template slot="next-text">
                    <i class="far fa-angle-right" />
                </template>
            </b-pagination>
            <div class="pagination-info">
                {{ currentPageItemsIndexInfo }}
            </div>
        </template>
        <property-search-footer />
    </div>

    <property-search-loading-state
            v-else-if="isLoading"
            :style="getClassStyle"
    />

    <property-search-null-state
            v-else
    />
</template>

<script>
    import '../../PropertyListingCard/PropertyListingCard.scss';
    import '../../PropertyListingCard/PropertyListingCardPagination.scss';
    import AppResourceCollection from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection";
    import AppResource from "@milestoneslabs/milestones-js-engine/src/Data/AppResource";
    import PropertySearchNullState from "../PropertySearchNullState/PropertySearchNullState";
    import PropertyListingCard from "../../PropertyListingCard/PropertyListingCard";
    import PropertyListingInfoCard from "../../PropertyListingCard/PropertyListingInfoCard";
    import CardLoadingPlaceholder
        from '@milestoneslabs/milestones-ui-kit/src/components/LoadingPlaceholder/Card/CardLoadingPlaceholder';
    import {
        PAGE_SIZE,
        PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED,
        PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES
    } from "../../GroupPropertySearchHeader/PropertySearchHeader/PropertySearchHeader";
    import PropertyListingHeader from "./PropertyListingHeader/PropertyListingHeader";
    import PropertySearchLoadingState
        from './../../GroupPropertyListing/PropertySearchLoadingState/PropertySearchLoadingState';
    import PropertySearchFooter from "../../PropertySearchFooter/PropertySearchFooter";

    import {arrayConcatAt,isNth} from '../../../../helpers/functions';

    export const PROPERTY_LISTING_PAGINATION_UPDATED = 'PropertyListing:pagination-updated';

    export default {
        name: "PropertyListing",
        components: {
            PropertySearchFooter,
            PropertySearchLoadingState,
            PropertySearchNullState,
            PropertyListingCard,
            PropertyListingInfoCard,
            PropertyListingHeader,
            CardLoadingPlaceholder,
        },
        props: {
            propertyDetailsRoute: {
                type: [String, Object],
                default: 'property.details.view',
            },
            hidden: {
                type: Boolean,
                default: false,
            },
            favoriteOptionEnabled: {
                type: Boolean,
                default: true,
            },
            displayInfoCards: {
                type: Boolean,
                default: false,
            },
            contentHeight: {
                type: Number,
                default: 0,
            }
        },
        data() {
            return {
                propertyAppResources: new AppResourceCollection('Property'),
                meta: null,
                searchedAddress: '',
                pageSize: PAGE_SIZE,
                isLoading: true,
            };
        },

        computed: {
            propertyAppResourcesLen() {
                return this.propertyAppResources.length;
            },
            total() {
                return (this.meta && this.meta.total) ?
                    this.meta.total : this.propertyAppResourcesLen;
            },
            currentPage() {
                return this.meta ? this.meta.currentPage : 1;
            },
            prevBtnClass() {
                return this.currentPage > 1 ? 'prev-next' : 'd-none';
            },
            nextBtnClass() {
                return this.total && this.pageSize && this.currentPage < Math.ceil(this.total/this.pageSize)
                    ? 'prev-next' : 'd-none';
            },
            currentPageItemsIndexInfo() {
                if (!this.meta) {
                    return '';
                }
                return this.total > this.pageSize ?
                    `${this.meta.from+1}-${this.meta.last} of ${this.total}` :
                    `${this.total} of ${this.total}`;
            },
            getClassStyle() {
                if (this.contentHeight !== 0) {
                    return {height: `calc(100vh - ${this.contentHeight}px)`};
                }

                return {};
            },
        },

        beforeMount() {
            this.$bus.$on(PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES, this.handleUpdatingProperties);
            this.$bus.$on(PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED, this.handlePropertiesUpdated);
        },

        beforeDestroy() {
            this.$bus.$off(PROPERTY_SEARCH_HEADER_UPDATING_PROPERTIES, this.handleUpdatingProperties);
            this.$bus.$off(PROPERTY_SEARCH_HEADER_PROPERTIES_UPDATED, this.handlePropertiesUpdated);
        },

        methods: {
            getCardType(resource) {
                if (resource.uuid) {
                    return 'PropertyListingCard';
                }
                if (resource.type === 'info-card') {
                    return 'PropertyListingCard';
                }
                return 'CardLoadingPlaceholder';
            },

            injectInfoCards: function(properties) {
                let firstAfter = 4,
                    afterEveryN = 10,
                    propertyCount = !properties ? 0 : properties.length,
                    infoCard = {
                        _uuid: null,
                        type: 'info-card'
                    };

                if (propertyCount>= firstAfter - 1) {
                    //inject info card firstAfter given position
                    properties = arrayConcatAt(properties, firstAfter, infoCard);
                }

                if (propertyCount>= afterEveryN - 1) {
                    //inject info card afterEveryN items
                    properties = arrayConcatAt(
                        properties,
                        function(n){
                            return isNth(afterEveryN)(
                                n - (firstAfter && n>firstAfter ? firstAfter+2 : 0)
                            );
                        },
                        infoCard
                    );
                }

                return properties;
            },

            handleUpdatingProperties() {
                this.isLoading = true;
            },

            /**
             * @param properties {array}
             * @param meta {AppResourceSearch}
             * @param search {string}
             */
            handlePropertiesUpdated({properties, meta, search}) {
                this.propertyAppResources = this.displayInfoCards
                    ? this.injectInfoCards(properties)
                    : properties;
                this.meta = meta;
                this.searchedAddress = search;
                this.isLoading = false;
            },

            /**
             * @param value {number}
             */
            moveToPage(value) {
                this.$refs.propertyListingContainer.scrollTop = 0;
                this.$bus.$emit(PROPERTY_LISTING_PAGINATION_UPDATED, value);
            },
        },
    };
</script>
